/**
 * audioTuning — single source of truth for every tunable audio value
 * (gains, fades, ducking, per-phase choreography).
 *
 * Persisted defaults live in tuning.json (written back by the /studio dev
 * endpoint). At runtime the studio mutates a live snapshot via applyTuning();
 * stagePlayer subscribes and re-ramps its gain nodes so slider changes are
 * audible instantly, without a reload.
 */
import type { Phase } from '@ringmaster/shared';
import defaults from './tuning.json';

/** Per-phase entry choreography: when the stinger fires and how long the
 *  narration queue is held so the stinger lands first. */
export interface BeatChoreo {
  stingerDelayMs: number;
  narrationDelayMs: number;
}

export interface AudioTuning {
  gains: {
    bed: number;
    layer: number;
    day: number;
    lobby: number;
    sfx: number;
    /** UI feedback one-shots (click/hover) — deliberately well under `sfx`. */
    ui: number;
    narration: number;
  };
  fades: {
    ambientCrossOutMs: number;
    ambientCrossInMs: number;
    ambientStopMs: number;
    dawnStopMs: number;
    unmountStopMs: number;
  };
  ducking: {
    /** Bed/layer gain multiplier while narration speaks (0..1). */
    bedLevel: number;
    /** One-shot bus gain multiplier while narration speaks (0..1). */
    sfxLevel: number;
    attackMs: number;
    releaseMs: number;
  };
  choreo: Partial<Record<Phase, BeatChoreo>>;
}

export type AudioTuningPatch = {
  [K in keyof AudioTuning]?: Partial<AudioTuning[K]>;
};

function clone(t: AudioTuning): AudioTuning {
  return JSON.parse(JSON.stringify(t)) as AudioTuning;
}

let current: AudioTuning = clone(defaults as AudioTuning);
const listeners = new Set<(t: AudioTuning) => void>();

/** Live tuning snapshot. Read at USE time (not module load) so studio edits apply. */
export function getTuning(): AudioTuning {
  return current;
}

/** Shallow-per-section merge + notify subscribers (stagePlayer re-ramps live nodes). */
export function applyTuning(patch: AudioTuningPatch): void {
  current = {
    gains: { ...current.gains, ...patch.gains },
    fades: { ...current.fades, ...patch.fades },
    ducking: { ...current.ducking, ...patch.ducking },
    choreo: { ...current.choreo, ...patch.choreo },
  };
  notify();
}

/** Replace the whole snapshot (studio Revert / load-from-server). */
export function setTuning(next: AudioTuning): void {
  current = clone(next);
  notify();
}

/** Back to the values shipped in tuning.json. */
export function resetTuning(): void {
  current = clone(defaults as AudioTuning);
  notify();
}

export function subscribeTuning(cb: (t: AudioTuning) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function notify(): void {
  for (const cb of listeners) {
    try {
      cb(current);
    } catch {
      /* a listener throwing must not break tuning updates */
    }
  }
}
